import i18next from "i18next"
import { Bike, ErrorResponse, ErrorFn, OperatorData, BikeOwner, BikeDescription } from "models"
import { http, getError, catchError } from "services/http"

export interface BikeAPIGenericResponse {
	bike_id: string
	row_version: string
}

/**********************
 **** GET BIKES ****
 **********************/

export const getBikes = async (): Promise<{ res: Bike[] } & ErrorFn> => {
	const bikesErrors = (error: ErrorResponse) => {
		switch (error?.code) {
			default:
				return {
					code: error?.code,
					message: i18next.t("error_default", { ns: "form" }),
				}
		}
	}

	const res = await http
		.get<Bike[]>("obikeapi/account/get_bikes")
		.then((res) => res.data)
		.catch((e) => catchError(e))

	return {
		res: res as Bike[],
		getError: "error" in res ? getError(res.error, bikesErrors) : null,
	}
}

/**********************
 **** GET BIKE ****
 **********************/

export const getBike = async (bikeId: string): Promise<{ res: Bike } & ErrorFn> => {
	const bikesErrors = (error: ErrorResponse) => {
		switch (error?.code) {
			case "RessourceNotFoundException": // Bike not found
				return {
					code: error?.code,
					message: i18next.t("error_bike_not_found", { ns: "form" }),
				}
			default:
				return {
					code: error?.code,
					message: i18next.t("error_default", { ns: "form" }),
				}
		}
	}

	const res = await http
		.get<Bike>(`obikeapi/account/get_bike?bike_id=${bikeId}`)
		.then((res) => res.data)
		.catch((e) => catchError(e))

	return {
		res: res as Bike,
		getError: getError(res as unknown as ErrorResponse, bikesErrors),
	}
}

/*********************
 **** UPDATE BIKE ****
 *********************/

export type UpdateBikeAnswer = Record<string, never>

export interface UpdateBikeProps {
	row_version: string
	bike_id: string
	operator_data?: OperatorData
	bike_status?: number
	bike_owner?: BikeOwner
}

export const updateBike = async (bike: UpdateBikeProps): Promise<ErrorFn> => {
	const updateBikeErrors = (error: ErrorResponse) => {
		switch (error?.code) {
			case "TooManyRequestsException": // This Bike is already up to date
				return {
					code: error?.code,
					message: i18next.t("bike_already_up_to_date", { ns: "form" }),
				}
			default:
				return {
					code: error?.code,
					message: i18next.t("error_default", { ns: "form" }),
				}
		}
	}

	const res = await http
		.put<UpdateBikeAnswer>("obikeapi/account/update_bike", bike)
		.then((res) => res.data)
		.catch((e) => catchError(e))
	return {
		getError: getError(res as unknown as ErrorResponse, updateBikeErrors),
	}
}

/***************************
 **** GET BIKE PASSWORD ****
 ***************************/

export type GetBikePasswordAnswer = {
	censor_mail?: string
	send_code_by_mail?: boolean
}

export const getBikePassword = async (
	bikeId: string
): Promise<{ res: GetBikePasswordAnswer } & ErrorFn> => {
	const bikeErrors = (error: ErrorResponse) => {
		switch (error?.code) {
			case "NotAuthorizedException":
				return {
					code: error?.code,
					message: i18next.t("bike_already_in_list", { ns: "form" }),
				}
			case "RessourceNotFoundException": // Bike not found
				return {
					code: error?.code,
					message: i18next.t("error_bike_not_found", { ns: "form" }),
				}
			case "UnauthorizedBikeId":
				return {
					code: error?.code,
					message: i18next.t("bike_not_available", { ns: "form" }),
				}
			case "BikeVersionMismatchException":
				return {
					code: error?.code,
					message: i18next.t("error_code_notuse", { ns: "form" }),
				}
			default:
				return {
					code: error?.code,
					message: i18next.t("error_default", {
						ns: "form",
					}),
				}
		}
	}
	const res = await http
		.get<GetBikePasswordAnswer>(`obikeapi/account/get_bike_password?bike_id=${bikeId}`, {})
		.then((res) => res.data)
		.catch((e) => catchError(e))

	return {
		res: res as GetBikePasswordAnswer,
		getError: getError(res as unknown as ErrorResponse, bikeErrors),
	}
}

/**************************
 **** CONFIRM ADD BIKE ****
 **************************/

export type ConfirmAddBikeAnswer = Record<string, never>

export interface ConfirmAddBikeProps {
	bike_id: string
	code?: string
	password?: string
}

export interface ConfirmAddBikeResponse {
	bike_id: string
	row_version: string
}

export const confirmAddBike = async (
	props: ConfirmAddBikeProps
): Promise<{ res: ConfirmAddBikeResponse } & ErrorFn> => {
	const confirmAddBikeErrors = (error: ErrorResponse) => {
		switch (error?.code) {
			case "NotAuthorizedException": // Code expired or invalid
				return {
					code: error?.code,
					message: i18next.t("expired_code", { ns: "form" }),
				}
			case "RessourceNotFoundException": // Bike not found
				return {
					code: error?.code,
					message: i18next.t("error_bike_not_found", { ns: "form" }),
				}
			default:
				return {
					code: error?.code,
					message: i18next.t("error_default", { ns: "form" }),
				}
		}
	}

	const res = await http
		.put<ConfirmAddBikeAnswer>("obikeapi/account/confirm_add_bike", props)
		.then((res) => res.data)
		.catch((e) => catchError(e))
	return {
		res: res as unknown as ConfirmAddBikeResponse,
		getError: getError(res as unknown as ErrorResponse, confirmAddBikeErrors),
	}
}

/******************************
 **** ACTION NOTIFICATIONS ****
 ******************************/

export interface UpdateBikeNotificationProps {
	bike_id: string
	bike_action_needed_code: string
	value: boolean
}

export const updateBikeNotification = async (
	updateBike: UpdateBikeNotificationProps
): Promise<{ res: BikeAPIGenericResponse } & ErrorFn> => {
	const updateBikeNotificationErrors = (error: ErrorResponse) => {
		switch (error?.code) {
			case "RessourceNotFoundException": // Bike not found
				return {
					code: error?.code,
					message: i18next.t("error_bike_not_found", { ns: "form" }),
				}
			case "TooManyRequestException": // This bike is already accepted
				return {
					code: error?.code,
					message: i18next.t("bike_already_up_to_date", { ns: "form" }),
				}
			case "BikeStatusMismatchException": // This bike is not on service
				return {
					code: error?.code,
					message: i18next.t("bike_not_in_service", { ns: "form" }),
				}
			case "BikeVersionMismatchException": // You are trying to modify an outdated version of this bike
				return {
					code: error?.code,
					message: i18next.t("bike_old_version", { ns: "form" }),
				}
			default:
				return {
					code: error?.code,
					message: i18next.t("error_default", { ns: "form" }),
				}
		}
	}

	const res = await http
		.put<BikeAPIGenericResponse>("obikeapi/account/bike/action_modif", updateBike)
		.then((res) => res.data)
		.catch((e) => catchError(e))
	return {
		res: res as unknown as BikeAPIGenericResponse,
		getError: getError(res as unknown as ErrorResponse, updateBikeNotificationErrors),
	}
}

/***********************************
 **** PROPERTY TRANSFER REQUEST ****
 ***********************************/

export interface PropertyTransferResponseProps {
	bike_id: string
	response: string
}

export const updatedPropertyTransferRequest = async (
	propertyTransferResponse: PropertyTransferResponseProps,
	token: string
): Promise<{ res: BikeAPIGenericResponse } & ErrorFn> => {
	const updatedPropertyTransferRequestErrors = (error: ErrorResponse) => {
		switch (error?.code) {
			default:
				return {
					code: error?.code,
					message: i18next.t("error_default", { ns: "form" }),
				}
		}
	}

	const config = {
		headers: { Authorization: `Bearer ${token}` },
	}

	const res = await http
		.put<BikeAPIGenericResponse>("obikeapi/fastaction/bike", propertyTransferResponse, config)
		.then((res) => res.data)
		.catch((e) => catchError(e))
	return {
		res: res as unknown as BikeAPIGenericResponse,
		getError: getError(res as unknown as ErrorResponse, updatedPropertyTransferRequestErrors),
	}
}

/******************************
 **** MIGRATE BICYCODE 2.0 ****
 ******************************/

type migrateBicycodeBikeProps = {
	bike_id: string
	bike_description: BikeDescription
	bike_owner: BikeOwner
	bike_status: number
	password: string
}

export const migrateBicycodeBike = async (
	registerBike: migrateBicycodeBikeProps
): Promise<ErrorFn> => {
	const migrateBicycodeBikeErrors = (error: ErrorResponse) => {
		switch (error?.code) {
			case "NotAuthorizedException": // Wrong bike_id or password
				return {
					code: error?.code,
					message: i18next.t("wrong_bike_or_password", { ns: "form" }),
				}
			case "RessourceNotFoundException": // Ressource not found
				return {
					code: error?.code,
					message: i18next.t("error_ressource_not_found", { ns: "form" }),
				}
			case "FnuciApiException": // <Fnuci_message_error>
				return {
					code: error?.code,
					message: i18next.t("fnuci_global_error", { ns: "form" }),
				}
			default:
				return {
					code: error?.code,
					message: i18next.t("error_default", { ns: "form" }),
				}
		}
	}
	const res = await http
		.post<BikeAPIGenericResponse>(
			"obikeapi/account/bike/migrate_bicycode_2_0",
			registerBike,
			{}
		)
		.then((res) => res.data)
		.catch((e) => catchError(e))
	return {
		getError: getError(res as unknown as ErrorResponse, migrateBicycodeBikeErrors),
	}
}

/******************************
 **** MODIFY BIKE PROPERTY ****
 ******************************/

export interface UpdateBikeOwnerProps {
	row_version: string
	bike_id: string
	new_owner?: BikeOwner
	cancel_transfer?: boolean
}

export const modifyBikeProperty = async (
	updateBikeOwner: UpdateBikeOwnerProps
): Promise<ErrorFn> => {
	const updateBikeOwnerErrors = (error: ErrorResponse) => {
		switch (error?.code) {
			case "MailNotAvailableException": // Mail must be different than yours.
				return {
					code: error?.code,
					message: i18next.t("set_different_email", { ns: "form" }),
				}
			case "BikeStatusMismatchException": // You can change bike property only if is synchronize with FNUCI or if bike is on service.
				return {
					code: error?.code,
					message: i18next.t("bike_not_sync_or_not_on_service", { ns: "form" }),
				}
			case "RessourceNotFoundException": // Bike not found
				return {
					code: error?.code,
					message: i18next.t("error_bike_not_found", { ns: "form" }),
				}
			case "BikeVersionMismatchException": // You are trying to modify an outdated version of this bike
				return {
					code: error?.code,
					message: i18next.t("bike_old_version", { ns: "form" }),
				}
			default:
				return {
					code: error?.code,
					message: i18next.t("error_default", { ns: "form" }),
				}
		}
	}

	const res = await http
		.put<BikeAPIGenericResponse>("obikeapi/account/bike/modify_bike_property", updateBikeOwner)
		.then((res) => res.data)
		.catch((e) => catchError(e))
	return {
		getError: getError(res as unknown as ErrorResponse, updateBikeOwnerErrors),
	}
}

/**********************************
 **** GET BIKE PORTABILITY INFO ****
 **********************************/

export const getBikePortabilityInfo = async (
	bikeId: string,
	transferCode: string,
	operator: string
): Promise<{ res: Bike } & ErrorFn> => {
	const bikesErrors = (error: ErrorResponse) => {
		switch (error?.code) {
			case "RessourceNotFoundException": // You did not request a transfer code for this bike.
				return {
					code: error?.code,
					message: i18next.t("portability.RessourceNotFoundException_error", {
						ns: "bike",
					}),
				}
			case "TooManyRequestsException": // Operation lockout due to multiple failed transfer code attempts.
				return {
					code: error?.code,
					message: i18next.t("portability.TooManyRequestsException_error", {
						ns: "bike",
					}),
				}
			case "FnuciApiException":
				switch (error?.message) {
					case "Transfer code expired.":
						return {
							code: error?.code,
							message: i18next.t(
								"portability.FnuciApiException_error_request_expired",
								{
									ns: "bike",
								}
							),
						}
					default:
						return {
							code: error?.code,
							message: i18next.t("portability.FnuciApiException_error_wrong_code", {
								ns: "bike",
							}),
						}
				}

			default:
				return {
					code: error?.code,
					message: i18next.t("error_default", { ns: "form" }),
				}
		}
	}

	const res = await http
		.get<Bike>(
			`obikeapi/fastaction/portability/bike?bike_id=${bikeId}&operator=${operator}&transfer_code=${transferCode}`
		)
		.then((res) => res.data)
		.catch((e) => catchError(e))

	return {
		res: res as Bike,
		getError: getError(res as unknown as ErrorResponse, bikesErrors),
	}
}

/***********************************
 **** PORTABILITY USER RESPONSE *****
 ***********************************/

export interface PortabilityUserResponseProps {
	bike_id: string
	operator: string
	transfer_code: string
	response: string
}

export const updatePortabilityUserResponse = async (
	portabilityUserResponse: PortabilityUserResponseProps
): Promise<{ res: BikeAPIGenericResponse } & ErrorFn> => {
	const bikesErrors = (error: ErrorResponse) => {
		switch (error?.code) {
			case "RessourceNotFoundException": // You did not request a transfer code for this bike.
				return {
					code: error?.code,
					message: i18next.t("portability.RessourceNotFoundException_error", {
						ns: "bike",
					}),
				}
			case "TooManyRequestsException": // Operation lockout due to multiple failed transfer code attempts.
				return {
					code: error?.code,
					message: i18next.t("portability.TooManyRequestsException_error", {
						ns: "bike",
					}),
				}
			case "FnuciApiException":
				switch (error?.message) {
					case "Transfer code expired.":
						return {
							code: error?.code,
							message: i18next.t(
								"portability.FnuciApiException_error_request_expired",
								{
									ns: "bike",
								}
							),
						}
					default:
						return {
							code: error?.code,
							message: i18next.t("portability.FnuciApiException_error_wrong_code", {
								ns: "bike",
							}),
						}
				}

			default:
				return {
					code: error?.code,
					message: i18next.t("error_default", { ns: "form" }),
				}
		}
	}

	const res = await http
		.put<BikeAPIGenericResponse>(
			"obikeapi/fastaction/portability/bike",
			portabilityUserResponse
		)
		.then((res) => res.data)
		.catch((e) => catchError(e))
	return {
		res: res as unknown as BikeAPIGenericResponse,
		getError: getError(res as unknown as ErrorResponse, bikesErrors),
	}
}
